import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const statCards = [
  { key: 'artists', label: 'Artists', accent: 'from-[#FF6F00] to-[#FF8F00]' },
  { key: 'verified_artists', label: 'Verified Artists', accent: 'from-[#00BFA5] to-[#00D9B5]' },
  { key: 'collaborators', label: 'Collaborators', accent: 'from-[#00BFA5] to-[#00D9B5]' },
  { key: 'videos', label: 'Videos', accent: 'from-[#4A148C] to-[#6A1B9A]' },
];

const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      try {
        const response = await adminApi.get('/admin/dashboard');
        if (mounted) {
          setDashboard(response.data);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className="text-gray-500">Loading dashboard...</div>;
  }

  const stats = dashboard?.stats || {};
  const readiness = dashboard?.launch_readiness || {};
  const pendingArtistReviews = readiness.pending_artist_reviews || 0;
  const pendingCollaboratorReviews = readiness.pending_collaborator_reviews || 0;
  const newSupportMessages = readiness.new_support_messages || 0;
  const needsAttention = pendingArtistReviews + pendingCollaboratorReviews + newSupportMessages > 0;

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Overview</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mt-4">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Admin dashboard</h2>
            <p className="text-gray-600 mt-3 max-w-2xl">
              A mobile-friendly control center for registrations, submissions, and platform operations.
            </p>
          </div>
          <div className="rounded-2xl bg-orange-50 px-5 py-4">
            <p className="text-sm text-gray-500">Signed in as</p>
            <p className="font-semibold text-gray-900">{dashboard?.admin?.name}</p>
            <p className="text-sm text-gray-500">{dashboard?.admin?.email}</p>
          </div>
        </div>
      </section>

      {needsAttention && (
        <section className="bg-amber-50 rounded-[1.75rem] border border-amber-200 p-5 sm:p-6 shadow-md">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-700">Needs review</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">New activity is waiting in admin</h3>
            </div>
            <Link to="/admin/alerts" className="btn-primary w-fit">Open Alerts</Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 mt-5">
            <Link to="/admin/artists" className="rounded-2xl bg-white px-4 py-3 border border-amber-100">
              <p className="text-2xl font-bold text-gray-900">{pendingArtistReviews}</p>
              <p className="text-sm text-gray-600">Pending artist profiles</p>
            </Link>
            <Link to="/admin/collaborators" className="rounded-2xl bg-white px-4 py-3 border border-amber-100">
              <p className="text-2xl font-bold text-gray-900">{pendingCollaboratorReviews}</p>
              <p className="text-sm text-gray-600">Pending collaborator profiles</p>
            </Link>
            <Link to="/admin/support" className="rounded-2xl bg-white px-4 py-3 border border-amber-100">
              <p className="text-2xl font-bold text-gray-900">{newSupportMessages}</p>
              <p className="text-sm text-gray-600">New support messages</p>
            </Link>
          </div>
        </section>
      )}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <div key={card.key} className="bg-white rounded-[1.75rem] p-5 shadow-md border border-orange-100">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.accent} mb-5`} />
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats[card.key] ?? 0}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="bg-white rounded-[1.75rem] p-6 shadow-md border border-orange-100">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h3 className="text-xl font-bold text-gray-900">Recent artists</h3>
            <Link to="/admin/artists" className="text-sm font-semibold text-[#4A148C] hover:text-[#FF6F00]">
              Manage artists
            </Link>
          </div>

          <div className="space-y-3">
            {(dashboard?.recent_artists || []).map((artist) => (
              <Link
                key={artist.id}
                to={`/admin/artists?artist=${encodeURIComponent(artist.id || artist.name || '')}`}
                className="rounded-2xl bg-gray-50 px-4 py-3 flex items-center justify-between gap-4 transition hover:bg-orange-50 hover:shadow-sm"
              >
                <div>
                  <p className="font-semibold text-gray-900">{artist.name}</p>
                  <p className="text-sm text-gray-500">{artist.category || 'Uncategorized'}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${artist.is_verified ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                  {artist.is_verified ? 'Verified' : 'Pending'}
                </span>
              </Link>
            ))}
            {dashboard?.recent_artists?.length === 0 && (
              <p className="text-sm text-gray-500">No artists have been registered yet.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-[1.75rem] p-6 shadow-md border border-orange-100">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h3 className="text-xl font-bold text-gray-900">Recent collaborators</h3>
            <Link to="/admin/collaborators" className="text-sm font-semibold text-[#4A148C] hover:text-[#FF6F00]">
              Manage collaborators
            </Link>
          </div>

          <div className="space-y-3">
            {(dashboard?.recent_collaborators || []).map((collaborator) => (
              <Link
                key={collaborator.id}
                to={`/admin/collaborators?collaborator=${encodeURIComponent(collaborator.id || collaborator.name || '')}`}
                className="rounded-2xl bg-gray-50 px-4 py-3 flex items-center justify-between gap-4 transition hover:bg-orange-50 hover:shadow-sm"
              >
                <div>
                  <p className="font-semibold text-gray-900">{collaborator.name}</p>
                  <p className="text-sm text-gray-500">{collaborator.organization_name || 'Organisation pending'}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${collaborator.is_verified ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                  {collaborator.is_verified ? 'Verified' : 'Pending'}
                </span>
              </Link>
            ))}
            {dashboard?.recent_collaborators?.length === 0 && (
              <p className="text-sm text-gray-500">No collaborators have been registered yet.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-[1.75rem] p-6 shadow-md border border-orange-100">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h3 className="text-xl font-bold text-gray-900">Recent videos</h3>
            <Link to="/admin/videos" className="text-sm font-semibold text-[#4A148C] hover:text-[#FF6F00]">
              Review videos
            </Link>
          </div>

          <div className="space-y-3">
            {(dashboard?.recent_videos || []).map((video) => (
              <div key={video.id} className="rounded-2xl bg-gray-50 px-4 py-3">
                <p className="font-semibold text-gray-900">{video.video_title}</p>
                <p className="text-sm text-gray-500 mt-1">{video.artist_name}</p>
              </div>
            ))}
            {dashboard?.recent_videos?.length === 0 && (
              <p className="text-sm text-gray-500">No videos have been submitted yet.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
